import { useState, type ReactNode, type FormEvent } from 'react';
import { useLocation, useNavigate, Navigate, Link } from 'react-router-dom';
import { changeExpiredPassword } from '../../auth/authApi.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { LoginShell } from '../../../../shared/auth/LoginShell.js';

/**
 * The destination EX-PWD-03 has always named and never had.
 *
 * The backend refuses a proven-but-stale credential with a single-use grant.
 * That grant is the only thing that authorises this screen, so it is carried
 * in router state rather than storage: nothing to leave behind, and a direct
 * visit has no grant and is sent back to sign in.
 */
interface ChangePasswordState {
  readonly changeToken?: string;
}

const CHANGE_PASSWORD_TOKEN_KEY = 'tradelogic.auth.change-password-token';

function readChangeTokenFromLocation(location: ReturnType<typeof useLocation>): string | null {
  const stateToken = (location.state as ChangePasswordState | null)?.changeToken;
  if (typeof stateToken === 'string' && stateToken.trim()) return stateToken.trim();

  const queryToken = new URLSearchParams(location.search).get('token');
  if (typeof queryToken === 'string' && queryToken.trim()) return queryToken.trim();

  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(CHANGE_PASSWORD_TOKEN_KEY);
    if (stored && stored.trim()) return stored.trim();
  } catch {
    // Session storage can be unavailable; the screen then behaves as the
    // backend expects: it has no grant and sends the user back to sign-in.
  }

  return null;
}

function clearChangeToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(CHANGE_PASSWORD_TOKEN_KEY);
  } catch {
    // The grant is already one-time and short-lived; if storage is unavailable,
    // nothing should be persisted by the client in the first place.
  }
}

export function ChangePasswordPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const changeToken = readChangeTokenFromLocation(location);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reached without a grant — there is nothing to spend, so do not pretend
  // otherwise.
  if (!changeToken) return <Navigate to="/auth/login" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t('auth.changePassword.mismatch'));
      return;
    }
    setSaving(true);
    try {
      await changeExpiredPassword(changeToken, password);
      clearChangeToken();
      setSuccess(true);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  if (success) {
    return (
      <main aria-label="change-password-success">
        <LoginShell
          title={t('auth.changePassword.successTitle')}
          subtitle={t('auth.changePassword.successBody')}
        >
          <button type="button" onClick={() => navigate('/auth/login', { replace: true })}>
            {t('auth.changePassword.backToLogin')}
          </button>
        </LoginShell>
      </main>
    );
  }

  return (
    <main aria-label="change-password">
      <LoginShell
        title={t('auth.changePassword.title')}
        subtitle={t('auth.changePassword.intro')}
      >
        {error && (
          <>
            <p role="alert">{error}</p>
            {/* A grant that has run out can only be replaced by signing in
                again, so the way to do that is on the screen that says so. */}
            <p>
              <Link to="/auth/login">{t('auth.changePassword.backToLogin')}</Link>
            </p>
          </>
        )}
        <form onSubmit={onSubmit} aria-label="change-password-form">
          <div className="login-field">
            <label htmlFor="new-password">{t('auth.changePassword.newPassword')}</label>
            <div className="login-control">
              <input
                id="new-password"
                name="newPassword"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
          </div>
          <div className="login-field">
            <label htmlFor="confirm-password">{t('auth.changePassword.confirm')}</label>
            <div className="login-control">
              <input
                id="confirm-password"
                name="confirmPassword"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </div>
          <button type="submit" disabled={saving}>
            {saving ? t('auth.changePassword.saving') : t('auth.changePassword.submit')}
          </button>
        </form>
      </LoginShell>
    </main>
  );
}
